import React, { useState, useRef, useCallback, ReactNode } from "react";
import { cn } from "../../styles";
import { mdiTextBoxSearchOutline, mdiClose, mdiLock } from "@mdi/js";
import { LocalizationScope } from "../../util/LocalizationScope";
import { Button, ButtonVariant, Typography } from "../../atoms";
import { ThemeColor } from "../../props";
import { processErrorContent } from "./helper";
import { Input } from "../Input";
import { BaseFormFieldProps, FormFieldConstraint } from "./index";
import { CommonValues, CommonValueOption } from "./CommonValues";

export interface DialogFieldValue {
  label?: string | null;
  value: any;
  domain?: string | null;
  isSingleMatch?: boolean;
}

export interface FormFieldDialogProps extends BaseFormFieldProps {
  constraints: FormFieldConstraint;
  value: DialogFieldValue | null;
  onChangeField: (
    id: string,
    value: DialogFieldValue | null,
    opts?: any,
  ) => void;
  placeholder?: string;
  commonValues?: CommonValueOption[];
  resolvedCommonValues?: {
    values: CommonValueOption[];
    domain?: string;
  } | null;
  onResolvedCommonValues?: (resolved: any) => void;
  /** Called when the dialog is opened */
  onOpen?: () => void;
  /** Render prop for the selection dialog */
  renderDialog: (ctx: {
    open: boolean;
    onClose: () => void;
    onClear: (() => void) | false;
    onSelect: (values: any, domain?: string, isSingleMatch?: boolean) => void;
  }) => ReactNode;
}

export const DialogField: React.FC<FormFieldDialogProps> = ({
  className,
  id,
  value,
  onChangeField,
  label = "Label",
  localKey,
  errors = [],
  touched,
  helper,
  helperKey,
  constraints,
  placeholder,
  disabled,
  keepHeight,
  noLabel,
  dense,
  commonValues,
  resolvedCommonValues,
  onOpen,
  renderDialog,
  readOnly,
}) => {
  const [open, setOpen] = useState(false);
  const persistedValue = useRef<DialogFieldValue | null>(null);

  const error = !open && touched && errors[0];

  const handleOpen = useCallback(() => {
    setOpen(true);
    persistedValue.current = value;
    onOpen?.();
  }, [value, onOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
    onChangeField(id, persistedValue.current);
  }, [id, onChangeField]);

  const handleClear = useCallback(() => {
    setOpen(false);
    onChangeField(id, null);
  }, [id, onChangeField]);

  const handleSelect = useCallback(
    (values: any, domain?: string, isSingleMatch?: boolean) => {
      setOpen(false);
      if (!values) return;
      const { label, value: val, data } = values;
      onChangeField(
        id,
        {
          label: label || null,
          value: val !== undefined ? val : values,
          domain: domain || null,
          isSingleMatch,
        },
        { data },
      );
    },
    [id, onChangeField],
  );

  const finalDisabled = disabled || value?.isSingleMatch;
  const showClear = !disabled && !readOnly && !constraints.presence && !!value;

  return (
    <div className={className}>
      {!noLabel && (
        <div className="flex">
          <Typography.Label
            htmlFor={id}
            className={cn(
              "block font-medium select-none",
              dense ? "mb-0.5 text-xs" : "mb-1 text-sm",
            )}
            content={label || { local: id }}
          />
          {!!constraints.presence && (
            <span className="text-red-500 ml-0.5 text-xs align-super">*</span>
          )}
        </div>
      )}
      <div
        className={cn(
          "flex gap-2 items-center cursor-pointer",
          finalDisabled && "opacity-50 pointer-events-none",
          readOnly && !finalDisabled && "pointer-events-none",
        )}
        onClick={finalDisabled || readOnly ? undefined : handleOpen}
      >
        <Input
          className="grow pointer-events-none"
          id={id}
          value={value?.label || ""}
          onChange={() => {}}
          placeholder={placeholder}
          disabled={finalDisabled}
          dense={dense}
          error={!!error}
        />
        <Button
          variant={ButtonVariant.text}
          color={ThemeColor.gray}
          onClick={handleOpen}
          iconPath={value?.isSingleMatch ? mdiLock : mdiTextBoxSearchOutline}
          dense
          muted
          tabIndex={-1}
          disabled={finalDisabled}
        />
      </div>
      {commonValues && resolvedCommonValues && (
        <CommonValues
          localKey={localKey}
          options={resolvedCommonValues.values}
          onSelect={(option) =>
            handleSelect(option, resolvedCommonValues.domain)
          }
          disabled={disabled}
        />
      )}
      {(helperKey || helper || keepHeight) && !error && (
        <LocalizationScope scope={localKey}>
          <Typography.P
            id={`${id}-helper`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.gray}
            content={helperKey ? { local: helperKey } : helper ? helper : null}
          >
            {" "}
          </Typography.P>
        </LocalizationScope>
      )}
      {error && (
        <LocalizationScope scope={[localKey, "::PSS"]}>
          <Typography.P
            id={`${id}-error`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.error}
            content={processErrorContent(error)}
          />
        </LocalizationScope>
      )}
      {renderDialog({
        open,
        onClose: handleClose,
        onClear: showClear && handleClear,
        onSelect: handleSelect,
      })}
    </div>
  );
};

DialogField.displayName = "FormField.Dialog";

export { DialogField as Dialog };
export interface DialogProps extends FormFieldDialogProps {}
